/**
 * EncounterLayout - full-page shell for the clinical encounter workspace.
 *
 * Intentionally has NO main nav. The encounter workspace owns the screen so
 * clinicians can focus on documentation. Same cream canvas and floating pills
 * as the rest of the practice app.
 *
 * Chrome:  Exit pill · Orange "Visit" pill · workbench tools · user menu
 * Content: <Outlet /> fills the remaining height (the page lays out its own cards)
 */
import { Outlet } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLogotype, UserMenu } from "@/components/layout/shell";
import {
  WorkbenchFloatingDock,
  WorkbenchHeaderControls,
  WorkbenchTabBarSlot,
} from "@/components/layout/workbench";
import {
  EncounterExitProvider,
  useEncounterExitGuard,
} from "@/components/layout/encounter-exit-provider";

const PILL = "flex h-12 shrink-0 items-center rounded-full bg-card shadow-[0_1px_2px_rgba(0,58,67,0.05)]";

function EncounterLayoutShell() {
  const { requestExit } = useEncounterExitGuard();

  return (
    <div className="app-shell relative flex h-screen w-full flex-col overflow-hidden bg-[var(--canvas)]">
      <header className="z-40 flex shrink-0 items-center gap-2 px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={requestExit}
          className={`${PILL} group gap-2 px-4 text-sm font-semibold text-[#003a43] transition-colors hover:bg-[#f8f3eb] dark:text-foreground dark:hover:bg-muted`}
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Exit visit
        </button>

        <div className={`${PILL} gap-2 pl-1.5 pr-4`}>
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f8f3eb] dark:bg-muted">
            <img src="/logo/logo.png" alt="" className="h-6 w-6" />
          </span>
          <div className="leading-tight">
            <BrandLogotype className="text-sm" />
            <p className="text-[11px] font-medium text-[#c4532f]">Clinical visit</p>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className={`${PILL} hidden px-1.5 md:flex`}>
            <WorkbenchHeaderControls className="gap-0.5 sm:gap-0.5 [&_button]:rounded-full [&_button]:ring-0" />
          </div>
          <UserMenu className="h-12 shrink-0 rounded-full border-0 bg-card pl-1.5 pr-3 shadow-[0_1px_2px_rgba(0,58,67,0.05)] hover:bg-card sm:pl-1.5 sm:pr-3.5" />
        </div>
      </header>

      <WorkbenchTabBarSlot />

      <main className="relative min-h-0 flex-1 overflow-hidden px-3 pb-3 sm:px-4">
        <Outlet />
      </main>

      <WorkbenchFloatingDock />
    </div>
  );
}

export default function EncounterLayout() {
  return (
    <EncounterExitProvider>
      <EncounterLayoutShell />
    </EncounterExitProvider>
  );
}
